import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Card,
  Avatar,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Container,
  alpha,
  useTheme,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { Link } from "react-router-dom";
import axios from "axios";
import { School, ArrowForward, ChevronLeft, ChevronRight, Verified as VerifiedIcon, Star as StarIcon } from "@mui/icons-material";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const BRAND_COLOR = "#1976D2";

export default function LandingTutors() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [tutors, setTutors] = useState([]);
  const [featuredTutors, setFeaturedTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const sliderRef = useRef(null);

  useEffect(() => {
    const fetchTutors = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/admin/landing-tutors`);
        const data = res.data || [];
        setTutors(data);

        // Limit featured tutors to exactly 3
        const shuffled = [...data].sort(() => Math.random() - 0.5).slice(0, 3);
        setFeaturedTutors(shuffled);
      } catch (err) {
        console.error("❌ Failed to fetch landing tutors:", err);
        setSnackbar({ open: true, message: "Failed to load tutors. Please try again.", severity: "error" });
      } finally {
        setLoading(false);
      }
    };
    fetchTutors();
  }, []);

  const scrollSlider = (direction) => {
    if (!sliderRef.current) return;
    const width = sliderRef.current.clientWidth * 0.8;
    sliderRef.current.scrollBy({ left: direction === "left" ? -width : width, behavior: "smooth" });
  };

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 12 }}>
        <CircularProgress size={60} thickness={4} sx={{ color: BRAND_COLOR }} />
      </Box>
    );

  if (tutors.length === 0)
    return (
      <Box sx={{ textAlign: "center", py: 12 }}>
        <School sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
        <Typography variant="h5" color="text.secondary">
          No featured tutors available yet.
        </Typography>
      </Box>
    );

  return (
    <Container maxWidth="xl" disableGutters sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, sm: 4 }, overflowX: "hidden" }}>
      {/* Header */}
      <Box textAlign="center" sx={{ mb: 8 }}>
        <Typography
          variant="h3"
          sx={{ fontWeight: 700, mb: 2, color: "#ffffff", [theme.breakpoints.down("sm")]: { fontSize: "1.6rem" } }}
        >
          Meet Our Expert Tutors
        </Typography>
        <Typography variant="subtitle1" sx={{ color: "#ffffff" }}>
          Learn from professionals who make education personal and inspiring.
        </Typography>
      </Box>

      {/* Tutors */}
      {isMobile ? (
        <Box sx={{ position: "relative" }}>
          {/* Slider Container */}
          <Box
            ref={sliderRef}
            sx={{
              display: "flex",
              gap: 3,
              overflowX: "auto",
              px: 1,
              pb: 2,
              scrollSnapType: "x mandatory",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {featuredTutors.map((tutor) => (
              <Box key={tutor.id} sx={{ scrollSnapAlign: "start", flexShrink: 0, width: "80%" }}>
                <TutorCard tutor={tutor} />
              </Box>
            ))}
          </Box>

          {/* Left Arrow */}
          <IconButton
            onClick={() => scrollSlider("left")}
            sx={{
              position: "absolute",
              top: "50%",
              left: 0,
              transform: "translateY(-50%)",
              backgroundColor: "rgba(255,255,255,0.8)",
              "&:hover": { backgroundColor: "rgba(255,255,255,1)" },
              zIndex: 2,
            }}
          >
            <ChevronLeft />
          </IconButton>

          {/* Right Arrow */}
          <IconButton
            onClick={() => scrollSlider("right")}
            sx={{
              position: "absolute",
              top: "50%",
              right: 0,
              transform: "translateY(-50%)",
              backgroundColor: "rgba(255,255,255,0.8)",
              "&:hover": { backgroundColor: "rgba(255,255,255,1)" },
              zIndex: 2,
            }}
          >
            <ChevronRight />
          </IconButton>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 4,
            flexWrap: "nowrap",
          }}
        >
          {featuredTutors.map((tutor) => (
            <Box key={tutor.id} sx={{ flex: "1 1 0", maxWidth: 320 }}>
              <TutorCard tutor={tutor} />
            </Box>
          ))}
        </Box>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: 3, fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

/* Tutor Card */
function TutorCard({ tutor }) {
  return (
    <Card
      sx={{
        width: "100%",
        mx: "auto",
        borderRadius: 5,
        boxShadow: "0 6px 25px rgba(0,0,0,0.08)",
        textAlign: "center",
        p: 3,
        transition: "all 0.3s ease",
        backgroundColor: "#fff",
        "&:hover": { transform: "translateY(-6px)", boxShadow: "0 12px 35px rgba(0,0,0,0.12)" },
      }}
    >
      {/* Avatar with Verification Badge */}
      <Box sx={{ position: "relative", mb: 2, display: "flex", justifyContent: "center" }}>
        <Avatar
          src={tutor.profile_img ? `${BASE_URL}/uploads/tutors/${tutor.profile_img}` : "/images/default-avatar.png"}
          alt={tutor.tutor_name}
          onError={(e) => { e.target.src = "/images/default-avatar.png"; }}
          sx={{
            width: 90,
            height: 90,
            border: `3px solid ${alpha(BRAND_COLOR, 0.5)}`,
            boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
          }}
        />

        {/* Verification Badge */}
        {tutor.is_verified && (
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              backgroundColor: "#1976D2",
              borderRadius: "50%",
              padding: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "3px solid white",
            }}
            title="Verified Tutor"
          >
            <VerifiedIcon sx={{ width: 20, height: 20, color: "white" }} />
          </Box>
        )}

        {/* Big Brand Badge */}
        {tutor.brand_classification === "big_brand_partner" && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              backgroundColor: "#FFD700",
              borderRadius: "50%",
              padding: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "3px solid white",
            }}
            title="Big Brand Partner"
          >
            <StarIcon sx={{ width: 20, height: 20, color: "#000" }} />
          </Box>
        )}
      </Box>

      <Typography variant="h6" fontWeight={700} sx={{ color: "#1e293b" }}>{tutor.tutor_name}</Typography>
      <Typography variant="body2" sx={{ color: "#64748b", mb: 1 }}>{tutor.name || "General Tutor"}</Typography>

      <Typography
        variant="body2"
        sx={{
          color: "#64748b",
          fontSize: "0.9rem",
          minHeight: 45,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {tutor.course_description || "Helping you achieve academic excellence with a personalized approach."}
      </Typography>

      <Button
        variant="contained"
        component={Link}
        to={`/tutor-details/${tutor.id}`}
        endIcon={<ArrowForward />}
        fullWidth
        sx={{
          mt: 3,
          py: 1.2,
          borderRadius: 3,
          textTransform: "none",
          fontWeight: 600,
          backgroundColor: BRAND_COLOR,
          boxShadow: `0 4px 15px ${alpha(BRAND_COLOR, 0.25)}`,
          "&:hover": { backgroundColor: "#1565C0", boxShadow: `0 8px 25px ${alpha(BRAND_COLOR, 0.35)}`, transform: "translateY(-2px)" },
          transition: "all 0.3s ease",
        }}
      >
        Enroll Now
      </Button>
    </Card>
  );
}